import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../services/api';

const AdminDashboard = ({ onLogout }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    title: '', description: '', price: '', category: '', image_url: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [menuRes, settingsRes] = await Promise.all([
        api.get('/api/admin/menu', { headers }),
        api.get('/api/settings', { headers })
      ]);
      setMenuItems(menuRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await api.get('/api/admin/menu', newItem, { headers });
      setNewItem({ title: '', description: '', price: '', category: '', image_url: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      await api.get(`/api/admin/menu/${editingItem.id}`, editingItem, { headers });
      setEditingItem(null);
      fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await api.get(`/api/admin/menu/${id}`, { headers });
        fetchData();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleSettingsUpdate = async () => {
    try {
      await api.get('/api/admin/settings', settings, { headers });
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/api/admin/upload', formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      return res.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-charcoal text-white p-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <button onClick={onLogout} className="bg-imperial-red px-4 py-2 rounded">Logout</button>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Settings */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-bold mb-4">Site Settings</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Announcement</label>
              <input
                type="text"
                value={settings.announcement || ''}
                onChange={(e) => setSettings({...settings, announcement: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Hero Text</label>
              <input
                type="text"
                value={settings.hero_text || ''}
                onChange={(e) => setSettings({...settings, hero_text: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          <button onClick={handleSettingsUpdate} className="mt-4 bg-imperial-red text-white px-4 py-2 rounded">
            Update Settings
          </button>
        </div>

        {/* Menu Management */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Menu Management</h2>
          
          {/* Add New Item */}
          <form onSubmit={handleAddItem} className="mb-8 p-4 bg-gray-50 rounded">
            <h3 className="text-lg font-semibold mb-4">Add New Item</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Title"
                value={newItem.title}
                onChange={(e) => setNewItem({...newItem, title: e.target.value})}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="number"
                step="0.01"
                placeholder="Price"
                value={newItem.price}
                onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="text"
                placeholder="Category"
                value={newItem.category}
                onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="text"
                placeholder="Image URL"
                value={newItem.image_url}
                onChange={(e) => setNewItem({...newItem, image_url: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <textarea
              placeholder="Description"
              value={newItem.description}
              onChange={(e) => setNewItem({...newItem, description: e.target.value})}
              className="w-full mt-4 px-3 py-2 border border-gray-300 rounded-md"
              rows="3"
            ></textarea>
            <button type="submit" className="mt-4 bg-imperial-red text-white px-4 py-2 rounded">
              Add Item
            </button>
          </form>

          {/* Menu Items List */}
          <div className="space-y-4">
            {menuItems.map(item => (
              <div key={item.id} className="border border-gray-200 rounded p-4">
                {editingItem && editingItem.id === item.id ? (
                  <form onSubmit={handleUpdateItem} className="space-y-2">
                    <input
                      type="text"
                      value={editingItem.title}
                      onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <textarea
                      value={editingItem.description}
                      onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows="2"
                    ></textarea>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        value={editingItem.price}
                        onChange={(e) => setEditingItem({...editingItem, price: e.target.value})}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                      />
                      <input
                        type="text"
                        value={editingItem.category}
                        onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <input
                      type="text"
                      value={editingItem.image_url}
                      onChange={(e) => setEditingItem({...editingItem, image_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    <div className="flex space-x-2">
                      <button type="submit" className="bg-green-500 text-white px-4 py-2 rounded">Save</button>
                      <button onClick={() => setEditingItem(null)} className="bg-gray-500 text-white px-4 py-2 rounded">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.description}</p>
                      <p className="text-sm">${item.price} - {item.category}</p>
                    </div>
                    <div className="space-x-2">
                      <button onClick={() => setEditingItem(item)} className="bg-blue-500 text-white px-3 py-1 rounded text-sm">Edit</button>
                      <button onClick={() => handleDeleteItem(item.id)} className="bg-red-500 text-white px-3 py-1 rounded text-sm">Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;